import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { dropDownStore, type ITabItem } from 'shared/ui'
import { logger, truthy } from 'shared/lib'
import { Contact } from 'entities/Contacts/model/Contact'
import { type IParamsCreateContact } from 'entities/Contacts/api/types'
import { CreateContactErrors } from 'entities/Subscription/ui/TrialContactsLimitModal'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationKey, type IIntegrationKey } from 'entities/Integrations/api/types'
import type { IContactAll } from 'entities/Contacts'
import { ContactsSearchStore } from 'widgets/ContactsSearch/store/contactsSearchStore'

export enum ContactsSearchVariantEnum {
  default = 'default',
  checkbox = 'checkbox',
}

export enum ContactsSearchListVariantEnum {
  list_contacts = 'list_contacts',
  help = 'help',
  create_contact = 'create_contact',
  selected_contacts = 'selected_contacts',
}

export const SELECTED_TAB_KEY = 'selected' as const

export interface IIntegrationsTab extends ITabItem {
  key: IIntegrationsTabKey
}

export type IIntegrationsTabKey = IIntegrationKey | typeof SELECTED_TAB_KEY

type IAllowedIntegrations = {
  key: IIntegrationsTabKey
  store: ContactsSearchStore
}

type IInitSearchParent = {
  disabledHelp: boolean
  disabledAddNumber: boolean
  paramsCreateContact: {
    forLink: boolean
    fromConversation: boolean
  }
  hiddenIntegrationsTabs: IIntegrationsTabKey[]
}

export class ContactsSearchParentStore {
  private _searchNumber = ''
  private _creatingNumberLoading = false
  private _tabsMap: Map<string, IIntegrationsTab> = new Map()
  private _selectedMap: Map<number, IContactAll> = new Map()
  private _activeTab: IIntegrationsTabKey = IntegrationKey.salesmsg
  private _allowedIntegrations: IAllowedIntegrations[]
  private _debounceFetchContacts
  private _loadingContactsLocal = false
  private _loadingContactsLocalSetTimeout: ReturnType<typeof setTimeout> | null = null
  private _disabledHelp = false
  private _disabledAddNumber = false
  private _paramsCreateContact = {
    forLink: true,
    fromConversation: true,
  }
  private _disposeReactionInitContacts: ReturnType<typeof reaction> | null = null
  private _disposeReactionInitSearchNumber: ReturnType<typeof reaction> | null = null

  constructor(private _entities: IEntitiesStore) {
    this.reactionInitContacts()
    this._allowedIntegrations = [
      {
        key: IntegrationKey.salesmsg,
        store: new ContactsSearchStore(IntegrationKey.salesmsg, this._entities),
      },
      {
        key: IntegrationKey.hubspot,
        store: new ContactsSearchStore(IntegrationKey.hubspot, this._entities),
      },
      {
        key: IntegrationKey.salesforce,
        store: new ContactsSearchStore(IntegrationKey.salesforce, this._entities),
      },
      {
        key: IntegrationKey.activecampaign,
        store: new ContactsSearchStore(IntegrationKey.activecampaign, this._entities),
      },
      {
        key: IntegrationKey.infusionsoft,
        store: new ContactsSearchStore(IntegrationKey.infusionsoft, this._entities),
      },
      {
        key: IntegrationKey.pipedrive,
        store: new ContactsSearchStore(IntegrationKey.pipedrive, this._entities),
      },

      {
        key: IntegrationKey.integrately,
        store: new ContactsSearchStore(IntegrationKey.integrately, this._entities),
      },
      ...this._entities.integrationsStore.connectedIntegrations
        .map((item) => {
          if (item.isContactIntegration) {
            return {
              key: item.key,
              store: new ContactsSearchStore(item.key, this._entities),
            }
          }
        })
        .filter(truthy),
      {
        key: SELECTED_TAB_KEY,
        store: new ContactsSearchStore(SELECTED_TAB_KEY, this._entities),
      },
    ]

    this._debounceFetchContacts = debounce(this.initContacts, 500)

    makeAutoObservable(this)
  }

  get loadingContactsLocal() {
    return this._loadingContactsLocal
  }

  get activeTab() {
    return this._activeTab
  }

  get searchNumber() {
    return this._searchNumber
  }

  get creatingNumberLoading() {
    return this._creatingNumberLoading
  }

  get allowedIntegrations() {
    return this._allowedIntegrations
  }

  get selectedCount() {
    return this._selectedMap.size
  }

  get hasSelected() {
    return !!this.selectedCount
  }

  get selectedList() {
    return Array.from(this._selectedMap.values())
  }

  isSelected(item: IContactAll) {
    return this._selectedMap.has(item.id)
  }

  init = (props: IInitSearchParent) => {
    this._disabledHelp = props.disabledHelp
    this._disabledAddNumber = props.disabledAddNumber
    this._paramsCreateContact = props.paramsCreateContact
    this._allowedIntegrations = this._allowedIntegrations.filter(
      (integration) => !props.hiddenIntegrationsTabs.includes(integration.key)
    )
  }

  initSelectedMap = (contacts: Map<number, Contact>) => {
    this._selectedMap = new Map(contacts)
  }

  initTabs = async (hiddenIntegretionsTabs: IIntegrationsTabKey[]) => {
    await this._entities.integrationsStore.fetchIntegrations()

    this._allowedIntegrations.forEach(({ key }) => {
      if (hiddenIntegretionsTabs.includes(key)) {
        return
      }

      if (key === IntegrationKey.salesmsg) {
        this._tabsMap.set(IntegrationKey.salesmsg, {
          key: IntegrationKey.salesmsg,
          name: 'Salesmsg',
          count: 0,
        })
      }

      const integration = this._entities.integrationsStore.getIntegration(key)

      if (integration?.isConnected) {
        if (hiddenIntegretionsTabs.includes(this._activeTab)) {
          this._activeTab = integration.key
        }

        this._tabsMap.set(integration.key, {
          key: integration.key,
          name: integration.name,
          count: 0,
        })
      }
    })

    this._entities.integrationsStore.connectedIntegrations.forEach((integration) => {
      if (this._tabsMap.has(integration.key) || hiddenIntegretionsTabs.includes(integration.key)) {
        return
      }

      if (integration.isContactIntegration) {
        const integrationStore = new ContactsSearchStore(integration.key, this._entities)

        this._allowedIntegrations.push({
          key: integration.key,
          store: integrationStore,
        })

        this._tabsMap.set(integration.key, {
          key: integration.key,
          name: integration.name,
          count: 0,
        })

        if (hiddenIntegretionsTabs.includes(this._activeTab)) {
          this._activeTab = integration.key
        }
      }
    })
  }

  updateTabItem = (key: string, props = {}) => {
    const tabItem = this._tabsMap.get(key)

    if (tabItem) {
      this._tabsMap.set(key, {
        ...tabItem,
        ...props,
      })
    }
  }

  debounceInitContacts = () => {
    this.initCancelTokenSource()

    if (!this.canSearch) return

    runInAction(() => {
      this._loadingContactsLocal = true

      if (this._loadingContactsLocalSetTimeout) {
        clearTimeout(this._loadingContactsLocalSetTimeout)
      }
    })

    this._debounceFetchContacts()

    this._loadingContactsLocalSetTimeout = setTimeout(() => {
      runInAction(() => {
        this._loadingContactsLocal = false
      })
    }, 500)
  }

  initContacts = () => {
    this._allowedIntegrations.forEach(async (item) => {
      const integration = this._entities.integrationsStore.getIntegration(item.key)

      if (item.key === IntegrationKey.salesmsg) {
        await item.store.fetch({ search: this._searchNumber })
        this.updateTabItem(item.key, {
          count: this.getTotal(item.key),
        })
      }

      if (integration?.isConnected) {
        await item.store.fetch({ search: this._searchNumber })
        this.updateTabItem(item.key, {
          count: this.getTotal(item.key),
        })
      }
    })

    if (this._searchNumber && this._entities.organizationStore.isActivityLoggingEnabled) {
      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.contact.searched',
        data: {
          search_query: this._searchNumber,
        },
      })
    }
  }

  handleSelectTab = (tab: IIntegrationsTab) => {
    this._activeTab = tab.key
  }

  toggleSelectedTab = () => {
    if (this._activeTab === SELECTED_TAB_KEY) {
      this._activeTab = IntegrationKey.salesmsg
      return
    }

    this._activeTab = SELECTED_TAB_KEY
  }

  handleCreateContactSearchNumber = async (
    callback: (item: Contact, params?: { isNew?: boolean }) => void = () => {},
    onError?: () => void
  ) => {
    if (this._creatingNumberLoading) return

    runInAction(() => {
      this._creatingNumberLoading = true
    })

    try {
      const contact = await this._entities.contactsStore.createContact(
        {
          number: this._searchNumber || '',
          ...this._paramsCreateContact,
        },
        this._paramsCreateContact.fromConversation
          ? {
              catchError: CreateContactErrors.trialLimit,
              onError,
            }
          : undefined
      )

      if (contact) callback(contact, { isNew: true })
    } catch (e) {
      logger.error(e)
    }

    runInAction(() => {
      this._creatingNumberLoading = false
    })
  }

  handleAddCheckboxItem = async (
    item: IContactAll,
    callback: (item: IContactAll) => void = () => {}
  ) => {
    if (this._selectedMap.has(item.id)) {
      this._selectedMap.delete(item.id)
    } else {
      this._selectedMap.set(item.id, item)
    }

    return callback(item)
  }

  handleAddDefaultItem = async (
    item: IContactAll,
    callback: (item: IContactAll, options?: { isNew?: boolean }) => void = () => {}
  ) => {
    if (this._creatingNumberLoading) return
    if (!(item instanceof Contact)) {
      runInAction(() => {
        this._creatingNumberLoading = true
      })

      // Open a modal window if the contact has two phone numbers.
      if (item.phoneCount > 1) {
        this._entities.contactsStore.contactChoosePhoneModalStore.openModal({
          contact: item,
          callback: callback,
          params: this._paramsCreateContact,
        })

        runInAction(() => {
          this._creatingNumberLoading = false
          dropDownStore.allHide()
        })
        return
      }

      const params: IParamsCreateContact = {
        number: item.phone || item.mobile_phone || item.phoneInfo,
        forLink: this._paramsCreateContact.forLink,
        fromConversation: this._paramsCreateContact.fromConversation,
        ...item.params,
      }

      const integration = this._entities.integrationsStore.getIntegration(item.integration_type)

      params.integration_id = integration?.id

      if (!this._paramsCreateContact.forLink) {
        callback(item)
        return
      }

      const isContactExist = await this._entities.contactsStore.getContactByNumber(
        item.national_number ?? ''
      )

      const contact = await this._entities.contactsStore.createContact(params)

      if (contact) callback(contact, { isNew: !isContactExist })

      runInAction(() => {
        this._creatingNumberLoading = false
      })

      return
    }

    callback(item)
  }

  handleAddItem = async (
    item: IContactAll,
    callback: (item: IContactAll) => void = () => {},
    type: ContactsSearchVariantEnum
  ) => {
    switch (type) {
      case ContactsSearchVariantEnum.checkbox:
        return this.handleAddCheckboxItem(item, callback)

      case ContactsSearchVariantEnum.default:
        return this.handleAddDefaultItem(item, callback)

      default:
        return this.handleAddDefaultItem(item, callback)
    }
  }

  handleInputSearch = (search: string) => {
    this._searchNumber = search
  }

  handleClearSelected = () => {
    if (this._activeTab === SELECTED_TAB_KEY) {
      this._activeTab = IntegrationKey.salesmsg
    }
    this._selectedMap.clear()
  }

  initCancelTokenSource = () => {
    this._allowedIntegrations.forEach(({ store }) => {
      store.cancelRequest.init()
    })
  }

  reset = () => {
    this._disposeReactionInitContacts?.()
    this._disposeReactionInitSearchNumber?.()
    this.resetCancelTokenSource()
  }

  resetCancelTokenSource = () => {
    this._allowedIntegrations.forEach(({ store }) => {
      store.cancelRequest.reset()
    })
  }

  reactionInitContacts = () => {
    this._disposeReactionInitContacts?.()
    this._disposeReactionInitContacts = reaction(
      () => this._searchNumber,
      () => {
        this.debounceInitContacts()
      }
    )
  }

  getTotal = (key: string) => {
    const allowedIntegration = this._allowedIntegrations.find((item) => item.key === key)

    if (allowedIntegration) {
      return allowedIntegration.store.total
    }

    return 0
  }

  get loadingContacts() {
    return Boolean(
      this._allowedIntegrations.map((item) => item.store.loading).filter((item) => item).length ||
        this._loadingContactsLocal
    )
  }

  get hasTotalContacts() {
    return Boolean(
      this._allowedIntegrations.map((item) => item.store.total).filter((item) => item).length
    )
  }

  get showTypeView() {
    if (this._activeTab === SELECTED_TAB_KEY) return ContactsSearchListVariantEnum.selected_contacts
    if (!this._searchNumber.length) return false
    if (this.loadingContacts) return ContactsSearchListVariantEnum.list_contacts
    if (!this._disabledHelp && this.canSearchHelp) return ContactsSearchListVariantEnum.help
    if (!this._disabledAddNumber && !this.hasTotalContacts && this._searchNumber)
      return ContactsSearchListVariantEnum.create_contact

    return ContactsSearchListVariantEnum.list_contacts
  }

  get canSearchHelp() {
    if (!this._searchNumber.length) return false

    return this._searchNumber.length <= 2
  }

  get canSearch() {
    return this._searchNumber.length >= 3
  }

  get tabs() {
    return Array.from(this._tabsMap.values())
  }
}
